// docs/41 PZ8: a puzzle room's mechanisms on the board, drawn so the room reads at a glance —
// plates and the block that can press them, levers that show which way they stand, statues and
// runes with their names under them, inscriptions on the walls, the lock's sigil by its door.
// The room's workings are the puzzle, so they are drawn only where a bought hint points
// (core/puzzle/reveal.ts): a wire from a driver to the gate it works (lit while that driver is on),
// a gate's pips for how many of its drivers it needs, the lock's pips for the runes woken. The
// fixture a hint is about glows.
//
// Frames are Oryx's (`picks.json`): pressure_plate 430, boulder 164, statues 376/378/379, rune
// circle 431, the open pit 434, the gold key (items 138), lily pads 112.
//
// docs/55 adds the other families: mirrors on stands, the lamp and its beam (the beam is physical
// state, drawn always), crystal eyes, braziers, the spring, troughs wet or dry, sluice wheels, water
// wheels (turning while the water runs), keys (on the floor, on their bearer, in their socket),
// sockets, portal stones (dim while they sleep), bells, dials showing their face, pits and filled
// pits, and ice underfoot.
//
// Most of these are the commissioned puzzle pieces (puzzleArt.ts, placeholder art until the
// artist's); each still has its vector drawing on the same palette, used where the atlas lacks it.
import Phaser from 'phaser';
import type { FixtureView, Tile } from '../../core/sim/types';
import { NO_REVEAL, wireKey, type HintReveal } from '../../core/puzzle/reveal';
import { ON_FRAMES, PUZZLE_ATLAS, atlasOf, cairnArt, channelPiece, creatureArt, dialArt, leverArt, openSides, statueFrame } from './puzzleArt';
import { curioFrame } from '../../core/snares/snares';
import { drawCurio } from './curioArt';
import { OBJECT_ATLAS, objectTable, objectVariant } from '../matter/objectArt';

const FRAMES = { plate: 'world_0430', block: 'world_0164', statue: 'world_0376', rune: 'world_0431' } as const;
/** docs/60: a stair down (steps into the dark, 67) or up (66); a shrine by its boon — the fountain heals, the altars bless and ward, the gem reveals. */
const STAIR_FRAMES = { down: 'world_0067', up: 'world_0066' } as const;
const SHRINE_FRAMES: Record<string, string> = { Heal: 'world_0486', Bless: 'world_0492', Ward: 'world_0491', Reveal: 'world_0488' };
const BOON_GLOW: Record<string, number> = { Heal: 0x6fe08a, Bless: 0xffd166, Ward: 0x8fd3ff, Reveal: 0xb48cff };
/** docs/50: a relic's look (fk-schema `RelicLook` order) → its frame: the items sheet, a coffer the world's chest. */
export const RELIC_FRAMES = ['item_101', 'item_029', 'item_089', 'item_067', 'item_045', 'item_138', 'item_149', 'item_133', 'item_148', 'item_085', 'item_023', 'item_096', 'item_199', 'item_151', 'item_145', 'item_127', 'world_0261'] as const;
/** fk-schema `TaskLook`, in order (docs/189: their own art is `task_<look>` in the objects atlas). */
export const TASK_LOOKS = ['Beacon', 'Crystal', 'Cauldron', 'Sarcophagus', 'Gate', 'Gravestone', 'Barrel', 'Chest', 'Throne', 'Boulder', 'Winch', 'Totem'] as const;
/** docs/50: a task's look (fk-schema `TaskLook` order) → its world frame. */
export const TASK_FRAMES = ['world_0099', 'world_0217', 'world_0374', 'world_0265', 'world_0258', 'world_0087', 'world_0268', 'world_0325', 'world_0322', 'world_0164', 'world_0433', 'world_0378'] as const;
const GOLD = 0xffd166, LIT = 0x8ff0a4, DIM = 0x6b6f7a, WIRE_OFF = 0x9aa3b5, INK = 0x1b1410, PARCH = 0xe9dcc0;
const BEAM = 0xfff2a8, WATER = 0x3f8fd8, FOAM = 0xbfe6ff, STONE = 0x8a8478, DARK = 0x24202a, FLAME = 0xff9a3c, ARCANE = 0xb48cff, ICE = 0xcdeeff;

/**
 * docs/106: the lock book's looks (`fk_forge::puzzle::book::look`), in order: a frame of the world
 * or item sheet, and how much of the square an item frame fills.
 */
export const PROP_FRAMES: readonly (readonly [string, number])[] = [
  ['world_0376', 1], ['world_0379', 1], ['world_0261', 1], ['world_0325', 1], ['world_0265', 1], ['world_0380', 1],
  ['world_0437', 1], ['world_0440', 1], ['world_0374', 1], ['world_0322', 1], ['world_0087', 1], ['world_0090', 1],
  ['world_0268', 1], ['world_0317', 1], ['world_0217', 1], ['world_0204', 1], ['candelabra', 1], ['item_007', 0.75],
  ['item_133', 0.75], ['item_067', 0.75], ['item_148', 0.75], ['item_149', 0.75], ['item_136', 0.75], ['item_138', 0.75],
  ['item_224', 0.8], ['item_089', 0.7], ['item_145', 0.75], ['world_0327', 1], ['item_152', 0.75], ['item_072', 0.75],
  ['item_099', 0.7], ['wonder-well', 1], ['item_019', 0.75], ['waystone', 1], ['item_100', 0.7], ['item_144', 0.75],
  ['item_092', 0.7], ['item_076', 0.7], ['sphinx', 1], ['world_0377', 1], ['world_0164', 1], ['world_0383', 1],
  // the commissioned puzzle pieces (`look::GEAR` on)
  ['gear', 1], ['spindle', 1], ['ring-bar', 1], ['flagstone', 1], ['mosaic', 1], ['court-square', 1],
  ['tablet', 1], ['world_0112', 1], ['peg-hole', 1], ['cairn', 1], ['basket', 1], ['boat', 1],
  ['coins', 1], ['balance', 1], ['spice', 1], ['loom', 1], ['monk', 1], ['courtier', 1], ['guildsman', 1],
  ['lord', 1], ['lady', 1], ['king', 1], ['knight', 1], ['squire', 1], ['traveller', 1], ['griffin', 1], ['portrait', 1],
  // the dials' looks (`look::COMBO_WHEEL` on)
  ['combo-wheel', 1], ['cipher-wheel', 1], ['wonder-orrery', 1], ['flowerbed', 1], ['horse', 1], ['wonder-tower', 1],
  ['crate', 1], ['brigand', 1], ['seer', 1], ['child', 1],
];

/** docs/55: water runs between these squares (a channel joins up with its neighbours). */
const WET_KINDS = new Set(['Channel', 'Spring', 'Valve', 'Wheel']);
/** docs/41: the locks judged by the bolt (a bolt seal); the rest are a sigil's eye. */
const BOLTED = new Set(['Pattern', 'Tally', 'Offering', 'Arrange', 'Measure', 'Machine']);

/** The short word a piece goes by on the board ("the heron statue — green bronze…" → "heron"). */
export function shortName(f: FixtureView): string {
  const head = f.name.split(' — ')[0]!.replace(/^the /, '');
  if (f.kind === 'Statue') return head.replace(/ statue$/, '');
  if (f.kind === 'Rune') return head.replace(/ rune$/, '');
  if (f.kind === 'Bell') return head.replace(/ bell$/, '');
  // docs/105: "the heron totem" → "heron"; a thing to give goes by its own name
  if (f.kind === 'Turntable' || f.kind === 'Bearer') return head.replace(/ \S+$/, '');
  if (f.kind === 'Idol') return head;
  // docs/106: "the barrel flask" → "barrel"; a one-word thing keeps its word
  if (f.kind === 'Prop' || f.kind === 'Switch' || f.kind === 'Jug' || f.kind === 'Token') return head.includes(' ') ? head.replace(/ \S+$/, '') : head;
  if (f.kind === 'Lever') {
    const m = /^(\w+) (lever|handle)$/.exec(head);
    return m && !/^(sluice|weir|winch|waking)$/.test(m[1]!) ? m[1]! : '';
  }
  return '';
}

/** docs/55: the face a dial shows ("the heron dial — moon · reed · wave", face 1 → "reed"). */
export function dialFace(f: FixtureView): string {
  const faces = (f.name.split(' — ')[1] ?? '').split(' · ');
  return faces[f.face ?? 0] ?? '';
}

export class FixtureLayer {
  private objects: Phaser.GameObjects.GameObject[] = [];
  private glow?: Phaser.GameObjects.Graphics;
  private sprites = new Map<number, Phaser.GameObjects.Image>();
  /** Frame cycles running on drawn pieces, stopped at the next draw. */
  private timers: Phaser.Time.TimerEvent[] = [];
  private fixtures: readonly FixtureView[] = [];
  private drawn: { reveal: HintReveal; ice: readonly Tile[] } = { reveal: NO_REVEAL, ice: [] };
  /** A carried key or relic, drawn on its fixture's square and shifted to wherever its bearer stands. */
  private carried: { box: Phaser.GameObjects.Container; holder: number; tile: Tile }[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly layer: Phaser.GameObjects.Layer,
    private readonly cell: number,
    private readonly dpr: number,
  ) {}

  private has(frame: string, atlas = 'world'): boolean {
    return this.scene.textures.exists(atlas) && this.scene.textures.get(atlas).has(frame);
  }

  /** A frame from whichever atlas holds it, centred on the square at `scale` of a cell. */
  private piece(t: Tile, frame: string, scale: number, dx = 0, dy = 0): Phaser.GameObjects.Image | undefined {
    const atlas = atlasOf(frame);
    if (!this.has(frame, atlas)) return undefined;
    const c = this.centre(t);
    const img = this.scene.add.image(c.x + dx, c.y + dy, atlas, frame);
    img.setScale((this.cell * scale) / Math.max(1, img.width));
    return this.add(img);
  }

  /** docs/189: an objects-atlas frame at one art pixel to a world pixel, its feet two pixels above the square's foot. */
  private objectPiece(t: Tile, frame: string): Phaser.GameObjects.Image | undefined {
    if (!this.has(frame, OBJECT_ATLAS.key)) return undefined;
    const k = this.cell / 24;
    const img = this.scene.add.image((t.x + 0.5) * this.cell, (t.y + 1) * this.cell - 2 * k, OBJECT_ATLAS.key, frame).setOrigin(0.5, 1).setScale(k);
    return this.add(img);
  }

  /** docs/106: a lock-book look: a world frame filling the square, an item frame set in it, or a plain stand. */
  private propLook(t: Tile, look: number, x: number, y: number, on = false): Phaser.GameObjects.Image | undefined {
    const [base, scale] = PROP_FRAMES[look] ?? PROP_FRAMES[0]!;
    const frame = on && ON_FRAMES[base] && this.has(ON_FRAMES[base]!, PUZZLE_ATLAS.key) ? ON_FRAMES[base]! : base;
    if (frame.startsWith('item_')) {
      const img = this.piece(t, frame, scale);
      if (img) return img;
    } else {
      const img = this.image(t, frame, () => {});
      if (img) return img;
    }
    const c = this.cell;
    this.add(this.scene.add.graphics()).fillStyle(STONE, 1).fillRoundedRect(x + 5, y + 4, c - 10, c - 8, 2);
    return undefined;
  }

  private add<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.layer.add(o);
    this.objects.push(o);
    return o;
  }

  private centre(t: Tile): { x: number; y: number } {
    return { x: t.x * this.cell + this.cell / 2, y: t.y * this.cell + this.cell / 2 };
  }

  private image(t: Tile, frame: string, fallback: (g: Phaser.GameObjects.Graphics) => void): Phaser.GameObjects.Image | undefined {
    const atlas = atlasOf(frame);
    if (this.has(frame, atlas)) return this.add(this.scene.add.image(t.x * this.cell, t.y * this.cell, atlas, frame).setOrigin(0, 0));
    const g = this.add(this.scene.add.graphics());
    fallback(g);
    return undefined;
  }

  /** A commissioned puzzle piece filling the square, turned `turns` quarters clockwise; undefined where the atlas lacks it. */
  private art(t: Tile, key: string, turns = 0, flip = false): Phaser.GameObjects.Image | undefined {
    if (!this.has(key, PUZZLE_ATLAS.key)) return undefined;
    const c = this.centre(t);
    const img = this.scene.add.image(c.x, c.y, PUZZLE_ATLAS.key, key).setDisplaySize(this.cell, this.cell);
    return this.add(img.setAngle(turns * 90).setFlipX(flip));
  }

  /** Shows `keys` in turn on one piece (a fire's flicker, a portal's swirl). */
  private cycle(img: Phaser.GameObjects.Image, keys: readonly string[], ms: number): void {
    let i = 0;
    this.timers.push(this.scene.time.addEvent({
      delay: ms, loop: true,
      callback: () => { i = (i + 1) % keys.length; if (img.active) img.setFrame(keys[i]!); },
    }));
  }

  private label(t: Tile, text: string, colour = '#f4ead2'): void {
    if (!text) return;
    const c = this.centre(t);
    const txt = this.scene.add.text(c.x, t.y * this.cell + this.cell + 0.5, text, {
      fontFamily: 'Cinzel, Georgia, serif', fontSize: '6px', fontStyle: '700', color: colour, stroke: '#0b0f1a', strokeThickness: 2,
    }).setOrigin(0.5, 0).setResolution(Math.max(4, this.dpr * 4));
    this.add(txt);
  }

  draw(fixtures: readonly FixtureView[] | undefined, reveal: HintReveal = NO_REVEAL, ice: readonly Tile[] = []): void {
    for (const o of this.objects) o.destroy();
    for (const e of this.timers) e.remove();
    this.objects = [];
    this.timers = [];
    this.sprites.clear();
    this.carried = [];
    this.fixtures = fixtures ?? [];
    this.drawn = { reveal, ice };
    const c = this.cell;
    const byId = new Map(this.fixtures.map((f) => [f.id, f] as const));
    // docs/55: ice underfoot, a pale glaze with a glint, under everything
    if (ice.length) {
      const g = this.add(this.scene.add.graphics());
      for (const t of ice) {
        const img = this.art(t, 'ice');
        if (img) { img.setAlpha(0.8); continue; }
        g.fillStyle(ICE, 0.28).fillRect(t.x * c, t.y * c, c, c);
        g.lineStyle(1, 0xffffff, 0.35).lineBetween(t.x * c + c * 0.2, t.y * c + c * 0.7, t.x * c + c * 0.45, t.y * c + c * 0.45);
      }
    }
    // beams: from the lamp through every square it lights (physical state, always drawn)
    const beams = this.add(this.scene.add.graphics());
    for (const f of this.fixtures) {
      if (f.kind !== 'Beacon' || !f.on || !f.beam?.length) continue;
      const pts = [this.centre(f.tile), ...f.beam.map((t) => this.centre(t))];
      for (const [w, a] of [[5, 0.18], [2, 0.85]] as const) {
        beams.lineStyle(w, BEAM, a);
        beams.beginPath();
        beams.moveTo(pts[0]!.x, pts[0]!.y);
        for (const p of pts.slice(1)) beams.lineTo(p.x, p.y);
        beams.strokePath();
      }
    }
    // wires first, under everything: driver → the gate (or lock) it works, where a hint showed it
    const wires = this.add(this.scene.add.graphics());
    for (const f of this.fixtures) {
      for (const d of f.drives) {
        const to = byId.get(d);
        if (!to || !reveal.wires.has(wireKey(f.id, d)) || (f.kind === 'Lock' && to.on)) continue;
        const a = this.centre(f.tile), b = this.centre(to.tile);
        wires.lineStyle(1, f.on ? GOLD : WIRE_OFF, f.on ? 0.9 : 0.7);
        dashed(wires, a.x, a.y, b.x, b.y, 3, 2);
      }
    }
    for (const f of this.fixtures) {
      const t = f.tile, x = t.x * c, y = t.y * c, mid = this.centre(t);
      const mark = this.objects.length;
      this.drawOne(f, t, x, y, mid);
      if (f.holder !== undefined && (f.kind === 'Key' || f.kind === 'Relic' || f.kind === 'Idol')) {
        // gathered into a box so it can ride along with its bearer (`follow`)
        const parts = this.objects.splice(mark);
        const box = this.add(this.scene.add.container(0, 0));
        for (const p of parts) { this.layer.remove(p); box.add(p); }
        this.carried.push({ box, holder: f.holder, tile: { ...t } });
      }
    }
  }

  private drawOne(f: FixtureView, t: Tile, x: number, y: number, mid: { x: number; y: number }): void {
    const c = this.cell;
    switch (f.kind) {
      case 'PressurePlate': {
        const heavy = (f.threshold ?? 0) > 2;
        // the heavy plate is an iron plate of its own (the pack's trap plate reads as a skull trap)
        // pressed, the plate sunk into the floor
        const pressed = f.on ? this.art(t, heavy ? 'plate-heavy-pressed' : 'plate-pressed') : undefined;
        const img = pressed ?? (heavy ? this.art(t, 'plate-heavy') : undefined)
          ?? this.image(t, FRAMES.plate, (g) => { g.lineStyle(1.5, heavy ? 0x8a8f99 : GOLD, 1).strokeRect(x + 4, y + 4, c - 8, c - 8); });
        if (img && !pressed) { if (f.on) img.setTint(LIT); else if (heavy && img.texture.key !== PUZZLE_ATLAS.key) img.setTint(0x9aa0aa); }
        if (f.on) this.add(this.scene.add.graphics()).lineStyle(1, LIT, 0.9).strokeRect(x + 2.5, y + 2.5, c - 5, c - 5);
        if (heavy) this.label(t, 'heavy', '#c9ced8');
        break;
      }
      case 'Block': {
        const heavy = (f.weight ?? 0) > 2;
        // slide() moves the image from its top-left, so the heavy block is placed as the plain one is
        const img = this.image(t, heavy && this.has('block-heavy', PUZZLE_ATLAS.key) ? 'block-heavy' : FRAMES.block, (g) => { g.fillStyle(0x7c7466, 1).fillRoundedRect(x + 3, y + 4, c - 6, c - 7, 3); });
        if (img) { this.sprites.set(f.id, img); if (heavy && img.texture.key !== PUZZLE_ATLAS.key) img.setTint(0xb8a890); }
        break;
      }
      case 'Statue': {
        this.image(t, statueFrame(f.name), (g) => { g.fillStyle(0xa9a39a, 1).fillRect(x + 7, y + 3, c - 14, c - 5); });
        this.label(t, shortName(f));
        // docs/55: a witness speaks — its words are on the room card
        if (f.name.split(' — ').length > 2) {
          const g = this.add(this.scene.add.graphics());
          g.fillStyle(PARCH, 1).fillRoundedRect(x + c - 9, y, 9, 6, 2).fillTriangle(x + c - 7, y + 5, x + c - 4, y + 5, x + c - 8, y + 8);
          g.fillStyle(INK, 1).fillCircle(x + c - 7, y + 3, 0.7).fillCircle(x + c - 4.5, y + 3, 0.7).fillCircle(x + c - 2, y + 3, 0.7);
        }
        break;
      }
      // docs/105: a figure on a turning plinth, looking at another of the lock's figures
      case 'Turntable': {
        const plinth = this.art(t, 'turntable');
        if (!plinth) this.image(t, FRAMES.statue, (g) => { g.fillStyle(0xa9a39a, 1).fillRect(x + 7, y + 3, c - 14, c - 5); });
        const g = this.add(this.scene.add.graphics());
        if (!plinth) g.lineStyle(1, 0x5b544a, 1).strokeEllipse(mid.x, y + c - 3, c * 0.7, 4);
        const lock = this.fixtures.find((l) => l.kind === 'Lock' && l.inputs?.includes(f.id));
        const others = (lock?.inputs ?? []).filter((i) => i !== f.id);
        const seen = this.fixtures.find((o) => o.id === others[f.face ?? 0]);
        if (seen) {
          const to = this.centre(seen.tile), a = Math.atan2(to.y - mid.y, to.x - mid.x), r = c * 0.48;
          const tx = mid.x + Math.cos(a) * r, ty = mid.y + Math.sin(a) * r;
          g.lineStyle(1.5, GOLD, 1).lineBetween(mid.x, mid.y, tx, ty);
          g.fillStyle(GOLD, 1).fillTriangle(tx, ty, tx - Math.cos(a - 0.5) * 3, ty - Math.sin(a - 0.5) * 3, tx - Math.cos(a + 0.5) * 3, ty - Math.sin(a + 0.5) * 3);
        }
        this.label(t, `${shortName(f)} → ${dialFace(f)}`);
        break;
      }
      // docs/105: a figure with empty hands, and the thing set in them
      case 'Bearer': {
        // docs/106: a bearer may be a shelf or another thing of the book
        const img = f.look ? this.propLook(t, f.look, x, y) : this.image(t, FRAMES.statue, (g) => { g.fillStyle(0xa9a39a, 1).fillRect(x + 7, y + 3, c - 14, c - 5); });
        if (img && !f.on) img.setTint(0xc9c2b4);
        this.label(t, shortName(f));
        break;
      }
      // docs/106: the lock book's pieces, each drawn as its look
      case 'Seat': {
        const img = this.propLook(t, f.look ?? 0, x, y);
        if (img) img.setAlpha(0.65);
        this.label(t, f.name.replace(/^the /, ''), '#c9ced8');
        break;
      }
      case 'Prop': case 'Switch': {
        if (f.on) this.add(this.scene.add.graphics()).fillStyle(f.kind === 'Switch' ? FLAME : LIT, 0.3).fillCircle(mid.x, mid.y, c * 0.48);
        const img = this.propLook(t, f.look ?? 0, x, y, f.on);
        if (img && f.on) img.setTint(f.kind === 'Switch' ? 0xffe0a0 : LIT);
        else if (img && f.kind === 'Switch') img.setTint(0x8a8f99);
        if (f.on && f.kind === 'Prop') this.add(this.scene.add.graphics()).lineStyle(1, LIT, 0.9).strokeCircle(mid.x, mid.y, c * 0.45);
        // docs/108: a gear says which way it is set to turn, sunwise (on) or widdershins
        if (f.kind === 'Switch' && PROP_FRAMES[f.look ?? 0]?.[0] === 'gear') turnArrow(this.add(this.scene.add.graphics()), mid.x, y + c * 0.12, c * 0.3, f.on);
        this.label(t, shortName(f), f.on ? '#b8ffc6' : '#f4ead2');
        break;
      }
      // docs/108: a machine's token, drawn as its look with the machine's state over it
      case 'Token': {
        const lock = this.fixtures.find((l) => l.kind === 'Lock' && l.inputs?.includes(f.id));
        if (f.on) this.add(this.scene.add.graphics()).fillStyle(LIT, 0.3).fillCircle(mid.x, mid.y, c * 0.48);
        // docs/108: the broken bridge's planks under a walker
        if (lock?.machine === 'Bridge') this.art(t, 'planks')?.setAlpha(0.9);
        const img = this.propLook(t, f.look ?? 0, x, y);
        if (img && f.on) img.setTint(LIT);
        const g = this.add(this.scene.add.graphics());
        const state = f.state ?? 0;
        const swap = (key: string | undefined) => { if (img && key && this.has(key, PUZZLE_ATLAS.key)) img.setTexture(PUZZLE_ATLAS.key, key); };
        // a passenger named for a creature rides as that creature
        if (lock?.machine === 'Ferry') swap(creatureArt(f.name));
        // a heap of stones as big as its count; the sliding floor's gap an empty socket
        if (lock?.machine === 'Nim') { swap(cairnArt(state)); if (!state) img?.setAlpha(0.35); }
        if (lock?.machine === 'Slide' && !state) swap('mosaic-gap');
        let text = shortName(f);
        switch (lock?.machine) {
          case 'Stack': {
            // the rings on the spindle, the great one at the foot
            const rings = [0, 1, 2, 3].filter((r) => (state >> r) & 1);
            rings.reverse().forEach((r, k) => {
              const w = 4 + r * 3;
              g.fillStyle(GOLD, 1).fillRoundedRect(mid.x - w / 2, y + c - 5 - k * 3, w, 2.5, 1);
            });
            break;
          }
          case 'Ferry': case 'Bridge': text = `${text} · ${state ? 'across' : 'here'}${f.on ? ' · aboard' : ''}`; break;
          case 'Nim': text = `${text} · ${state}`; break;
          case 'Rings': if (img && !state) img.setAlpha(0.45); text = `${text} · ${state ? 'on' : 'off'}`; break;
          case 'Jump': if (!state || !this.art(t, 'peg')) g.fillStyle(state ? GOLD : DARK, 1).fillCircle(mid.x, mid.y, c * 0.16); break;
          case 'Toads': {
            if (state) this.art(t, state === 1 ? 'toad-up' : 'toad-down')?.setScale(this.cell / 24 * 0.8);
            text = state === 1 ? `${text} · up` : state === 2 ? `${text} · down` : `${text} · empty`;
            break;
          }
          case 'Slide': text = (f.name.split(' — ')[1] ?? '').split(' · ')[state] ?? ''; break;
          default: break;
        }
        this.label(t, text, f.on ? '#b8ffc6' : '#f4ead2');
        break;
      }
      case 'Jug': {
        const img = this.propLook(t, 41, x, y);
        if (img && f.on) img.setTint(LIT);
        // what it holds of what it can hold (the sim's numbers)
        const g = this.add(this.scene.add.graphics());
        const fill = (f.cap ?? 1) > 0 ? (f.holds ?? 0) / (f.cap ?? 1) : 0;
        g.fillStyle(DARK, 0.8).fillRect(x + c - 5, y + 3, 3, c - 6);
        g.fillStyle(WATER, 1).fillRect(x + c - 5, y + 3 + (c - 6) * (1 - fill), 3, (c - 6) * fill);
        this.label(t, `${shortName(f)} ${f.holds ?? 0}/${f.cap ?? 0}`, f.on ? '#b8ffc6' : '#f4ead2');
        break;
      }
      case 'Idol': {
        const g = this.add(this.scene.add.graphics());
        // lying loose it sits in the middle of its square; carried or held it rides small in a corner
        const small = f.on || f.holder !== undefined;
        const ix = small ? x + c - 6 : mid.x, iy = small ? y + 6 : mid.y, k = small ? 0.6 : 1;
        if (this.piece(t, 'idol', small ? 0.45 : 1, ix - mid.x, iy - mid.y)) { if (!small) this.label(t, shortName(f)); break; }
        g.fillStyle(0x3b2a1e, 1).fillEllipse(ix, iy + 4 * k, 9 * k, 3 * k);
        g.fillStyle(GOLD, 1).fillRoundedRect(ix - 3 * k, iy - 4 * k, 6 * k, 8 * k, 2 * k);
        g.lineStyle(1, INK, 0.7).strokeRoundedRect(ix - 3 * k, iy - 4 * k, 6 * k, 8 * k, 2 * k);
        if (!small) this.label(t, shortName(f));
        break;
      }
      case 'Rune': {
        const woken = f.on ? this.art(t, 'rune-lit') : undefined;
        const img = woken ?? this.image(t, FRAMES.rune, (g) => { g.lineStyle(1.5, 0x5fd3e6, 1).strokeCircle(mid.x, mid.y, c * 0.32); });
        if (img && !woken) { if (f.on) img.setTint(LIT); else img.setAlpha(0.75); }
        if (f.on) this.add(this.scene.add.graphics()).fillStyle(LIT, 0.25).fillCircle(mid.x, mid.y, c * 0.45);
        this.label(t, shortName(f), f.on ? '#b8ffc6' : '#f4ead2');
        break;
      }
      case 'Lever': {
        // a post with a handle thrown left (lowered) or right (raised)
        const lever = leverArt(f.name, f.on);
        if (this.art(t, lever.key, 0, lever.flip)) {
          this.label(t, f.on ? `${shortName(f)} ▲`.trim() : `${shortName(f)} ▼`.trim(), f.on ? '#b8ffc6' : '#f4ead2');
          break;
        }
        const g = this.add(this.scene.add.graphics());
        g.fillStyle(0x3b2a1e, 1).fillRect(mid.x - 4, y + c - 7, 8, 4);
        const ang = f.on ? -0.6 : -2.55;
        const len = c * 0.42, bx = mid.x, by = y + c - 7;
        const hx = bx + Math.cos(ang) * len, hy = by + Math.sin(ang) * len;
        g.lineStyle(2, 0xc9b38a, 1).lineBetween(bx, by, hx, hy);
        g.fillStyle(f.on ? LIT : 0xd0574a, 1).fillCircle(hx, hy, 2.2);
        this.label(t, f.on ? `${shortName(f)} ▲`.trim() : `${shortName(f)} ▼`.trim(), f.on ? '#b8ffc6' : '#f4ead2');
        break;
      }
      case 'Inscription': {
        if (this.art(t, f.on ? 'inscription-read' : 'inscription')) break;
        const g = this.add(this.scene.add.graphics());
        g.fillStyle(PARCH, f.on ? 0.55 : 1).fillRect(x + 5, y + 6, c - 10, c - 11);
        g.lineStyle(1, INK, 0.8);
        for (let i = 0; i < 3; i++) g.lineBetween(x + 7, y + 9 + i * 3, x + c - 7, y + 9 + i * 3);
        break;
      }
      case 'Lock': {
        const g = this.add(this.scene.add.graphics());
        // an eye for the sigil that judges, a bar for the bolt
        const bolted = BOLTED.has(f.lock ?? '');
        if (!this.art(t, `${bolted ? 'seal-bolt' : 'seal-sigil'}${f.on ? '-open' : ''}`)) {
          g.fillStyle(0x24202a, 1).fillCircle(mid.x, mid.y, c * 0.36);
          g.lineStyle(1.5, f.on ? LIT : GOLD, 1).strokeCircle(mid.x, mid.y, c * 0.36);
          if (bolted) g.fillStyle(f.on ? LIT : GOLD, 1).fillRect(mid.x - c * 0.22, mid.y - 1.5, c * 0.44, 3);
          else { g.fillStyle(f.on ? LIT : GOLD, 1).fillEllipse(mid.x, mid.y, c * 0.36, c * 0.2); g.fillStyle(0x24202a, 1).fillCircle(mid.x, mid.y, 1.8); }
        }
        if (f.lock === 'Order' && f.inputs?.length && this.drawn.reveal.locks.has(f.id)) {
          // one pip per rune woken so far
          for (let i = 0; i < f.inputs.length; i++) g.fillStyle(i < (f.progress ?? 0) ? LIT : DIM, 1).fillCircle(x + 5 + i * ((c - 10) / Math.max(1, f.inputs.length - 1)), y + c - 3, 1.5);
        }
        break;
      }
      case 'Mirror': {
        if (this.art(t, f.on ? 'mirror' : 'mirror-back')) break;
        const g = this.add(this.scene.add.graphics());
        g.fillStyle(DARK, 1).fillRect(mid.x - 5, y + c - 6, 10, 3);
        const r = c * 0.36;
        // '/' runs bottom-left to top-right
        const [x1, y1, x2, y2] = f.on ? [mid.x - r, mid.y + r, mid.x + r, mid.y - r] : [mid.x - r, mid.y - r, mid.x + r, mid.y + r];
        g.lineStyle(4, 0x5b6170, 1).lineBetween(x1, y1, x2, y2);
        g.lineStyle(2, 0xe8f1ff, 1).lineBetween(x1, y1, x2, y2);
        g.fillStyle(0xffffff, 0.9).fillCircle((x1 + x2) / 2 + 1, (y1 + y2) / 2 - 1, 1);
        break;
      }
      case 'Beacon': {
        const g = this.add(this.scene.add.graphics());
        if (f.on) g.fillStyle(BEAM, 0.25).fillCircle(mid.x, mid.y, c * 0.48);
        if (!this.art(t, f.on ? 'beacon-lit' : 'beacon')) {
          g.fillStyle(0x3b2a1e, 1).fillRect(mid.x - 5, mid.y - 6, 10, 12);
          g.fillStyle(f.on ? BEAM : DIM, 1).fillCircle(mid.x, mid.y, 3.5);
        }
        // the way it shines, over the lamp
        this.layer.bringToTop(g);
        const d = { N: [0, -1], E: [1, 0], S: [0, 1], W: [-1, 0] }[f.dir as 'N' | 'E' | 'S' | 'W'] ?? [1, 0];
        g.fillStyle(GOLD, 1).fillTriangle(mid.x + d[0]! * c * 0.46, mid.y + d[1]! * c * 0.46, mid.x + d[0]! * c * 0.26 - d[1]! * 4, mid.y + d[1]! * c * 0.26 - d[0]! * 4, mid.x + d[0]! * c * 0.26 + d[1]! * 4, mid.y + d[1]! * c * 0.26 + d[0]! * 4);
        break;
      }
      case 'Eye': {
        const g = this.add(this.scene.add.graphics());
        if (f.on) g.fillStyle(0x7ff3ff, 0.3).fillCircle(mid.x, mid.y, c * 0.5);
        if (this.art(t, f.on ? 'eye-lit' : 'eye')) break;
        g.fillStyle(f.on ? 0x9ff8ff : 0x4a6b78, 1).fillTriangle(mid.x, y + 3, x + c - 5, mid.y, mid.x, y + c - 3);
        g.fillTriangle(mid.x, y + 3, x + 5, mid.y, mid.x, y + c - 3);
        g.lineStyle(1, f.on ? 0xffffff : 0x9aa3b5, 0.9).strokeTriangle(mid.x, y + 3, x + c - 5, mid.y, mid.x, y + c - 3).strokeTriangle(mid.x, y + 3, x + 5, mid.y, mid.x, y + c - 3);
        break;
      }
      case 'Brazier': {
        const g = this.add(this.scene.add.graphics());
        if (f.on) g.fillStyle(FLAME, 0.25).fillCircle(mid.x, mid.y - 2, c * 0.5);
        const fire = this.art(t, f.on ? 'brazier-lit-1' : 'brazier');
        if (fire) { if (f.on) this.cycle(fire, ['brazier-lit-1', 'brazier-lit-2'], 260); break; }
        g.fillStyle(0x4a3a2c, 1).fillRect(mid.x - 2, mid.y + 2, 4, c * 0.3);
        g.fillStyle(STONE, 1).fillEllipse(mid.x, mid.y + 2, c * 0.6, c * 0.22);
        if (f.on) {
          g.fillStyle(FLAME, 1).fillTriangle(mid.x - 6, mid.y + 1, mid.x + 6, mid.y + 1, mid.x, y + 2);
          g.fillStyle(0xffe08a, 1).fillTriangle(mid.x - 3, mid.y + 1, mid.x + 3, mid.y + 1, mid.x, y + 7);
        } else {
          // cold coals, and a pale rim so a dark brazier still reads as one
          g.fillStyle(0x3a3634, 1).fillEllipse(mid.x, mid.y, c * 0.42, c * 0.14);
          g.fillStyle(0x7a6a5a, 1).fillCircle(mid.x - 3, mid.y - 1, 1.2).fillCircle(mid.x + 2, mid.y - 1, 1.2);
          g.lineStyle(1, 0xd8ccb0, 0.9).strokeEllipse(mid.x, mid.y + 2, c * 0.62, c * 0.24);
        }
        break;
      }
      case 'Spring': {
        if (this.art(t, 'spring')) break;
        const g = this.add(this.scene.add.graphics());
        g.fillStyle(STONE, 1).fillCircle(mid.x, mid.y, c * 0.42);
        g.fillStyle(WATER, 1).fillCircle(mid.x, mid.y, c * 0.3);
        g.lineStyle(1, FOAM, 0.9).strokeCircle(mid.x, mid.y, c * 0.18).strokeCircle(mid.x, mid.y, c * 0.08);
        break;
      }
      case 'Channel': {
        // the trough joins up with the water squares beside it
        const water = this.fixtures.filter((o) => o.id !== f.id && WET_KINDS.has(o.kind)).map((o) => o.tile);
        const { key, turns } = channelPiece(openSides(t, water));
        if (this.art(t, f.on ? `${key}-wet` : key, turns)) break;
        const g = this.add(this.scene.add.graphics());
        g.lineStyle(1.5, 0x5b544a, 1).strokeRect(x + 2, y + 2, c - 4, c - 4);
        if (f.on) {
          g.fillStyle(WATER, 0.85).fillRect(x + 3, y + 3, c - 6, c - 6);
          g.lineStyle(1, FOAM, 0.8).lineBetween(x + 6, mid.y - 2, x + c - 8, mid.y - 4).lineBetween(x + 8, mid.y + 4, x + c - 6, mid.y + 2);
        } else {
          g.fillStyle(0x6e665a, 0.6).fillRect(x + 3, y + 3, c - 6, c - 6);
        }
        break;
      }
      case 'Valve': {
        if (this.art(t, f.on ? 'valve-open' : 'valve')) break;
        const g = this.add(this.scene.add.graphics());
        g.lineStyle(2, f.on ? FOAM : 0xd0574a, 1).strokeCircle(mid.x, mid.y, c * 0.32);
        for (let i = 0; i < 4; i++) {
          const a = (i * Math.PI) / 2 + (f.on ? Math.PI / 4 : 0);
          g.lineBetween(mid.x, mid.y, mid.x + Math.cos(a) * c * 0.32, mid.y + Math.sin(a) * c * 0.32);
        }
        g.fillStyle(GOLD, 1).fillCircle(mid.x, mid.y, 2);
        break;
      }
      case 'Wheel': {
        // drawn about its own centre so a turning wheel can spin
        const wheel = this.art(t, 'wheel');
        if (wheel) { if (f.on) this.scene.tweens.add({ targets: wheel, angle: 360, duration: 2400, repeat: -1 }); break; }
        const g = this.add(this.scene.add.graphics({ x: mid.x, y: mid.y }));
        const r = c * 0.42;
        g.lineStyle(2, 0x8b6b45, 1).strokeCircle(0, 0, r);
        for (let i = 0; i < 8; i++) {
          const a = (i * Math.PI) / 4;
          g.lineBetween(0, 0, Math.cos(a) * r, Math.sin(a) * r);
          g.fillStyle(0x6b4f33, 1).fillRect(Math.cos(a) * r - 2, Math.sin(a) * r - 2, 4, 4);
        }
        if (f.on) this.scene.tweens.add({ targets: g, angle: 360, duration: 2400, repeat: -1 });
        break;
      }
      case 'Key': {
        const g = this.add(this.scene.add.graphics());
        // on its bearer it rides small in the corner of the square; set in its socket it is drawn there
        const carried = f.holder !== undefined;
        const kx = carried ? x + c - 6 : mid.x, ky = carried ? y + 5 : mid.y, k = carried ? 0.6 : 1;
        // set in its socket, the filled socket shows it
        if (f.on && !carried && this.has('socket-filled', PUZZLE_ATLAS.key)) break;
        if (this.piece(t, 'item_138', carried ? 0.45 : 0.7, kx - mid.x, ky - mid.y)) break;
        g.lineStyle(2 * k, GOLD, 1).strokeCircle(kx - 3 * k, ky, 2.5 * k);
        g.lineBetween(kx - 0.5 * k, ky, kx + 6 * k, ky).lineBetween(kx + 4 * k, ky, kx + 4 * k, ky + 3 * k).lineBetween(kx + 6 * k, ky, kx + 6 * k, ky + 2.5 * k);
        break;
      }
      case 'Socket': {
        if (this.art(t, f.on ? 'socket-filled' : 'socket')) break;
        const g = this.add(this.scene.add.graphics());
        g.fillStyle(STONE, 1).fillRect(x + 4, y + 6, c - 8, c - 9);
        g.lineStyle(1, 0x5b544a, 1).strokeRect(x + 4, y + 6, c - 8, c - 9);
        g.fillStyle(f.on ? GOLD : DARK, 1).fillCircle(mid.x, mid.y, 2.5).fillRect(mid.x - 1, mid.y, 2, 4);
        if (f.on) this.add(this.scene.add.graphics()).lineStyle(1, LIT, 0.9).strokeRect(x + 2.5, y + 4.5, c - 5, c - 6);
        break;
      }
      case 'Portal': {
        const g = this.add(this.scene.add.graphics());
        if (f.on) g.fillStyle(ARCANE, 0.22).fillCircle(mid.x, mid.y, c * 0.5);
        const arch = this.art(t, f.on ? 'portal-on-1' : 'portal');
        if (arch) { if (f.on) this.cycle(arch, ['portal-on-1', 'portal-on-2'], 420); break; }
        g.fillStyle(0x6f6a62, 1).fillRect(x + 4, y + 4, 4, c - 7).fillRect(x + c - 8, y + 4, 4, c - 7).fillRect(x + 4, y + 3, c - 8, 3);
        g.fillStyle(f.on ? ARCANE : DARK, f.on ? 0.85 : 1).fillEllipse(mid.x, mid.y + 1, c * 0.34, c * 0.56);
        if (f.on) g.lineStyle(1, 0xe6dcff, 0.9).strokeEllipse(mid.x, mid.y + 1, c * 0.2, c * 0.36);
        break;
      }
      case 'Bell': {
        const g = this.add(this.scene.add.graphics());
        if (f.on) g.fillStyle(LIT, 0.25).fillCircle(mid.x, mid.y, c * 0.45);
        if (!this.art(t, f.on ? 'bell-rung' : 'bell')) {
          g.fillStyle(0x3b2a1e, 1).fillRect(x + 4, y + 3, c - 8, 2);
          g.fillStyle(f.on ? 0xffe08a : 0xc9a24a, 1).fillTriangle(mid.x, y + 5, x + 6, y + c - 6, x + c - 6, y + c - 6);
          g.fillStyle(0x3b2a1e, 1).fillCircle(mid.x, y + c - 5, 1.6);
        }
        this.label(t, shortName(f), f.on ? '#b8ffc6' : '#f4ead2');
        break;
      }
      case 'Dial': {
        // docs/106: a moon dial or an hourglass shows its face as a picture
        const picture = dialArt(dialFace(f));
        if (picture && this.art(t, picture)) { this.label(t, dialFace(f)); break; }
        // docs/106: a lock book's dial wears its look (a combination wheel, a horse, a seer), its face under it
        if (f.look !== undefined && this.has(PROP_FRAMES[f.look]?.[0] ?? '', atlasOf(PROP_FRAMES[f.look]?.[0] ?? ''))) {
          this.propLook(t, f.look, x, y);
          this.label(t, `${shortName(f)} · ${dialFace(f)}`.replace(/^ · /, ''));
          break;
        }
        const g = this.add(this.scene.add.graphics());
        // the face's middle and reach: the stone dial's face, else the drawn one
        const art = this.art(t, 'dial');
        const fx = art ? x + c * 0.48 : mid.x, fy = art ? y + c * 0.31 : mid.y - 1, r = art ? c * 0.2 : c * 0.24;
        if (!art) {
          g.fillStyle(STONE, 1).fillRect(mid.x - 5, y + c - 7, 10, 5);
          g.fillStyle(0xd8ccb0, 1).fillCircle(fx, fy, c * 0.34);
          g.lineStyle(1, 0x5b544a, 1).strokeCircle(fx, fy, c * 0.34);
        }
        this.layer.bringToTop(g);
        const n = Math.max(1, f.faces ?? 1);
        for (let i = 0; i < n; i++) {
          const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
          g.fillStyle(i === (f.face ?? 0) ? 0xd0574a : 0x8a8478, 1).fillCircle(fx + Math.cos(a) * r, fy + Math.sin(a) * r, 1.3);
        }
        const a = -Math.PI / 2 + ((f.face ?? 0) * 2 * Math.PI) / n;
        g.lineStyle(1.5, 0xd0574a, 1).lineBetween(fx, fy, fx + Math.cos(a) * r * 0.9, fy + Math.sin(a) * r * 0.9);
        this.label(t, dialFace(f));
        break;
      }
      case 'Pit': {
        if (f.on ? this.art(t, 'pit-filled') : this.image(t, 'world_0434', () => {})) break;
        const g = this.add(this.scene.add.graphics());
        if (f.on) {
          // a block sunk level with the floor
          g.fillStyle(0x7c7466, 1).fillRect(x + 1, y + 1, c - 2, c - 2);
          g.lineStyle(1, 0x5b544a, 1).strokeRect(x + 1.5, y + 1.5, c - 3, c - 3).lineBetween(x + 1, mid.y, x + c - 1, mid.y);
        } else {
          g.fillStyle(0x07060a, 0.92).fillRect(x + 1, y + 1, c - 2, c - 2);
          g.lineStyle(1, 0x3a3634, 1).strokeRect(x + 1.5, y + 1.5, c - 3, c - 3);
        }
        break;
      }
      case 'Relic': {
        // docs/50: what the stakes are about, lying unmarked, or a small token on the one who
        // carries it
        const frame = RELIC_FRAMES[f.look ?? 0] ?? RELIC_FRAMES[0];
        if (f.on) {
          const g = this.add(this.scene.add.graphics());
          g.fillStyle(0x0b0f1a, 0.85).fillCircle(x + c - 5, y + 5, 4.5);
          g.lineStyle(1, GOLD, 1).strokeCircle(x + c - 5, y + 5, 4.5);
          if (!this.piece(t, frame, 0.32, c / 2 - 5, -c / 2 + 5)) g.fillStyle(GOLD, 1).fillCircle(x + c - 5, y + 5, 2);
        } else if (!this.piece(t, frame, 0.7)) {
          this.add(this.scene.add.graphics()).fillStyle(GOLD, 1).fillCircle(mid.x, mid.y, c * 0.18);
        }
        break;
      }
      case 'Task': {
        // docs/50: the thing to work at, with a pip for each blow it needs
        const frame = TASK_FRAMES[f.look ?? 0] ?? TASK_FRAMES[0];
        // docs/189 EO: an objective of ours (`task_<TaskLook>` in the objects atlas), feet on its square's foot
        const own = objectVariant(objectTable(this.scene), `task_${TASK_LOOKS[f.look ?? 0] ?? 'Beacon'}`, 0);
        const img = own ? this.objectPiece(t, own.frame) : this.piece(t, frame, 1);
        if (!img) this.add(this.scene.add.graphics()).fillStyle(0x8a7a5a, 1).fillRoundedRect(x + 4, y + 4, c - 8, c - 8, 3);
        if (img && f.on) img.setTint(0x9aa0aa);
        const need = f.need ?? 0, done = f.done ?? 0;
        if (need > 0) {
          const g = this.add(this.scene.add.graphics());
          for (let i = 0; i < need; i++) {
            const px = mid.x - (need - 1) * 2.5 + i * 5;
            g.fillStyle(i < done ? LIT : 0x2a2f3a, 1).fillCircle(px, y + 3, 1.8);
            g.lineStyle(0.75, GOLD, 0.9).strokeCircle(px, y + 3, 1.8);
          }
        }
        this.label(t, f.name.replace(/^the /, ''), f.on ? '#b8ffc6' : '#f4ead2');
        break;
      }
      case 'Stair': {
        // docs/60 DC9: the stair fills its square; an arrow says which way it goes
        const img = this.image(t, f.down ? STAIR_FRAMES.down : STAIR_FRAMES.up, (g) => {
          g.fillStyle(0x24202a, 1).fillRect(x + 2, y + 2, c - 4, c - 4);
          for (let i = 0; i < 4; i++) g.fillStyle(0x8a8478, 1).fillRect(x + 3 + i * ((c - 6) / 4), y + 3 + (f.down ? i : 3 - i) * ((c - 6) / 5), (c - 6) / 4 - 1, c - 6);
        });
        img?.setTint(f.down ? 0xd8d0c0 : 0xffffff);
        const g = this.add(this.scene.add.graphics());
        const ax = x + c - 5, ay = y + c - 5;
        g.fillStyle(0x0b0f1a, 0.85).fillCircle(ax, ay, 4);
        g.fillStyle(GOLD, 1);
        if (f.down) g.fillTriangle(ax - 2.6, ay - 1.4, ax + 2.6, ay - 1.4, ax, ay + 2.2);
        else g.fillTriangle(ax - 2.6, ay + 1.4, ax + 2.6, ay + 1.4, ax, ay - 2.2);
        this.label(t, f.down ? 'down' : 'up', '#ffe39a');
        break;
      }
      case 'Shrine': {
        // docs/60 DC10: a shrine by its boon, glowing while it has uses left; spent, it is grey stone
        const spent = (f.uses ?? 0) === 0;
        const glow = BOON_GLOW[f.boon ?? 'Heal'] ?? GOLD;
        if (!spent) this.add(this.scene.add.graphics()).fillStyle(glow, 0.22).fillCircle(mid.x, mid.y, c * 0.55);
        // spent: the commissioned dry fountain or cracked altar, else the shrine greyed
        const dry = spent ? this.piece(t, `shrine-${(f.boon ?? 'Heal').toLowerCase()}-spent`, 1) : undefined;
        const img = dry ?? this.piece(t, SHRINE_FRAMES[f.boon ?? 'Heal'] ?? SHRINE_FRAMES['Heal']!, 1);
        if (!img) {
          const g = this.add(this.scene.add.graphics());
          g.fillStyle(STONE, 1).fillRect(x + 4, y + c * 0.45, c - 8, c * 0.45);
          g.fillStyle(spent ? DIM : glow, 1).fillCircle(mid.x, y + c * 0.38, c * 0.16);
        } else if (spent && !dry) img.setTint(0x777777).setAlpha(0.8);
        if (!spent && (f.uses ?? 0) > 0) {
          const g = this.add(this.scene.add.graphics());
          for (let i = 0; i < (f.uses ?? 0); i++) { const px = mid.x - ((f.uses ?? 1) - 1) * 2.5 + i * 5; g.fillStyle(glow, 1).fillCircle(px, y + 2.5, 1.6); g.lineStyle(0.75, 0x0b0f1a, 0.9).strokeCircle(px, y + 2.5, 1.6); }
        }
        this.label(t, f.boon ? f.boon.toLowerCase() : 'shrine', spent ? '#9aa3b5' : '#e9fff0');
        break;
      }
      case 'Curio': {
        // docs/140 DT3: a trap's curio in plain sight in its guise — the commissioned piece, else a
        // clear drawing of what it is; a used one sits dimmed
        if (!f.curio) break;
        const img = this.art(t, curioFrame(f.curio));
        if (img) { if (f.on) img.setTint(0x8a8a8a); break; }
        drawCurio(this.add(this.scene.add.graphics()), f.curio, x, y, c, f.on);
        break;
      }
      case 'Landmark': {
        // docs/50: the place the stakes are about, named on its first square
        this.label(t, f.name.replace(/^the /, ''), '#ffe39a');
        break;
      }
      case 'Gate': case 'Door': {
        // pips over the gate: how many of its drivers it needs, how many stand on
        if (f.on || !f.needed || !this.drawn.reveal.gates.has(f.id)) break;
        const on = this.fixtures.filter((d) => d.drives.includes(f.id) && d.on).length;
        const g = this.add(this.scene.add.graphics());
        for (let i = 0; i < f.needed; i++) {
          const px = mid.x - (f.needed - 1) * 2.5 + i * 5;
          g.fillStyle(i < on ? LIT : 0x2a2f3a, 1).fillCircle(px, y + 3, 1.8);
          g.lineStyle(0.75, GOLD, 0.9).strokeCircle(px, y + 3, 1.8);
        }
        break;
      }
    }
  }

  /** Carried keys and relics stay on their bearers as they walk (`at`: a unit's feet, as UnitSprite sits). */
  follow(at: (unit: number) => { x: number; y: number } | undefined): void {
    const c = this.cell;
    for (const k of this.carried) {
      const p = at(k.holder);
      if (p) k.box.setPosition(p.x - c / 2 - k.tile.x * c, p.y - c - k.tile.y * c);
    }
  }

  /** A key or relic taken up as the pulse plays (the next snapshot redraws it the same). */
  take(fixture: number, unit: number): void {
    if (!this.fixtures.some((f) => f.id === fixture && (f.kind === 'Key' || f.kind === 'Relic' || f.kind === 'Idol'))) return;
    this.redraw(this.fixtures.map((f) => (f.id === fixture ? { ...f, holder: unit, on: f.kind === 'Relic' ? true : f.on } : f)));
  }

  /** A key set into its socket as the pulse plays. */
  place(socket: number, key: number): void {
    const at = this.fixtures.find((f) => f.id === socket)?.tile;
    if (!at) return;
    this.redraw(this.fixtures.map((f) => {
      if (f.id === socket) return { ...f, on: true };
      if (f.id !== key) return f;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- the holder is dropped
      const { holder: _, ...rest } = f;
      return { ...rest, tile: { ...at }, on: true };
    }));
  }

  private redraw(fixtures: readonly FixtureView[]): void { this.draw(fixtures, this.drawn.reveal, this.drawn.ice); }

  /** Light the fixture a hint or the panel points at (undefined: none). */
  focus(id: number | undefined): void {
    this.glow?.destroy();
    this.glow = undefined;
    const f = id === undefined ? undefined : this.fixtures.find((x) => x.id === id);
    if (!f) return;
    const c = this.cell, mid = this.centre(f.tile);
    const g = this.scene.add.graphics();
    g.lineStyle(1.5, GOLD, 1).strokeCircle(mid.x, mid.y, c * 0.62);
    this.layer.add(g);
    this.glow = g;
    this.scene.tweens.add({ targets: g, alpha: 0.35, duration: 520, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
  }

  /** docs/41: a block slides to its new square as the pulse plays (the next snapshot redraws it). */
  slide(fixture: number, to: Tile, ms: number): void {
    const img = this.sprites.get(fixture);
    if (!img) return;
    this.scene.tweens.add({ targets: img, x: to.x * this.cell, y: to.y * this.cell, duration: Math.max(1, ms), ease: 'Quad.easeOut' });
  }

  /** The fixture on a square, if any (the board's hover card). */
  at(t: Tile): FixtureView | undefined {
    // Landmarks, carried keys and sunk blocks leave clicks to whatever stands on their square.
    const here = this.fixtures.filter((f) => f.tile.x === t.x && f.tile.y === t.y && f.kind !== 'Landmark' && f.kind !== 'Sunk' && !((f.kind === 'Key' || f.kind === 'Idol') && f.holder !== undefined) && !(f.kind === 'Idol' && f.on));
    // docs/106: a seat is floor under whatever stands on it
    return here.find((f) => f.kind !== 'Seat') ?? here[0];
  }
}

/** A curved arrow over the top of a square: sunwise (clockwise) or widdershins. */
function turnArrow(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, sunwise: boolean): void {
  const from = Math.PI * 1.15, to = Math.PI * 1.85;
  g.lineStyle(1.5, GOLD, 1).beginPath().arc(cx, cy + r, r, from, to, false).strokePath();
  const end = sunwise ? to : from, tx = cx + Math.cos(end) * r, ty = cy + r + Math.sin(end) * r;
  const d = sunwise ? 1 : -1;
  g.fillStyle(GOLD, 1).fillTriangle(tx + d * 2.5, ty + 0.5, tx - d * 1, ty - 2.5, tx - d * 1, ty + 2.5);
}

function dashed(g: Phaser.GameObjects.Graphics, x1: number, y1: number, x2: number, y2: number, dash: number, gap: number): void {
  const len = Math.hypot(x2 - x1, y2 - y1);
  if (len === 0) return;
  const ux = (x2 - x1) / len, uy = (y2 - y1) / len;
  for (let d = 0; d < len; d += dash + gap) {
    const e = Math.min(len, d + dash);
    g.lineBetween(x1 + ux * d, y1 + uy * d, x1 + ux * e, y1 + uy * e);
  }
}
